import { Data, Modal as ModalModule, UI, Utils } from 'common';
import React, { useContext } from 'react';
import { PendingPlanClosingDisclaimer } from '../changePlan/PendingPlanClosingDisclaimer.component';
import CancellationBanner from './CancellationBanner.component';
import CloseAccountSection from './CloseAccountSection.component';
import {
	Plan,
	useSubscriptionPlanState,
} from './hooks/useSubscriptionPlanState';
import LegacyPlanChangeConfirmationModal from './LegacyPlanChangeConfirmationModal.component';
import PlanFeaturesTable from './PlanFeaturesTable.component';
import PlanSummaryCard from './PlanSummaryCard.component';
import PlanUpdatedSuccessModal from './PlanUpdatedSuccessModal.component';
import { isInTrialBillingPeriod, NEW_LINEUP_PLAN_IDS } from './planRules';
import ReviewDeleteUsersModal from './ReviewDeleteUsersModal.component';
import SubscriptionCard from './SubscriptionCard.component';
import UpgradeToMultiLocationModal from './UpgradeToMultiLocationModal.component';

const { Section, SectionBody, Spinner, Typography, InlineMessage } = UI;
const { BillingPlans, Session, UserRole, Util } = Utils;
const { AccountStatus, Capability, PlanIds } = Data;
const { ModalContext } = ModalModule;

interface ManageSubscriptionPlanProps {
	flags?: { useBrightback?: boolean };
	onNavigate: (path: string, state?: any) => void;
	locationState?: { focusSeats?: boolean };
}

const ManageSubscriptionPlan = ({
	flags,
	onNavigate,
	locationState,
}: ManageSubscriptionPlanProps) => {
	const focusSeats = locationState?.focusSeats ?? false;
	const { state, actions, derived, currentPlanCardRef } =
		useSubscriptionPlanState();
	const { showModal } = useContext(ModalContext) as any;

	const { accountStatus, closeRequestedDate } = Session.getAccountInfo();
	const role = (Session as any).getUserRole() as string;
	const hasChangeServicePlan = (Session as any).hasCapability(
		Capability.ChangeServicePlan
	) as boolean;
	const isUserRole =
		role === UserRole.SingleUser ||
		role === UserRole.MultiUser ||
		role === UserRole.CorpUser;
	const readOnly = isUserRole && !hasChangeServicePlan;

	const { planDetails, nextBillingDate } =
		((Session as any).getAccountInfo() as {
			planDetails?: { planId?: number; pendingPlanId?: number };
			nextBillingDate?: string;
		}) ?? {};
	// New-lineup users always render the new grid. GBB users (CoreV380,
	// OfficeV381, EcommerceV383) and PAYG users also get the new grid
	// alongside their (read-only) legacy card — but only when the new lineup
	// is actually offered to them, i.e. the Professional plan is present in
	// the billing-plans response. Otherwise (and for truly old pre-GBB plans)
	// they keep the single-column legacy layout.
	// Keep in sync with GBB_PLAN_IDS in React/app/src/lib/api/hooks/account/subscription/types.ts
	const gbbPlanIds: number[] = [
		PlanIds.CoreV380,
		PlanIds.OfficeV381,
		PlanIds.EcommerceV383,
	];
	const isOnGbbPlan =
		planDetails?.planId != null &&
		gbbPlanIds.indexOf(planDetails.planId) !== -1;
	const isOnPaygPlan = BillingPlans.isUserInPaygPlan();
	// 385 is the legacy multi-location plan; no named constant exists in PlanIds
	const LEGACY_MULTI_LOCATION = 385;
	const isOnLegacyMultiLocation = planDetails?.planId === LEGACY_MULTI_LOCATION;
	const professionalAvailable = (BillingPlans.plans || []).some(
		p => p.PlanId === PlanIds.Professional403
	);
	const isNewLineup =
		BillingPlans.isNewLineupPlan() ||
		((isOnGbbPlan || isOnPaygPlan || isOnLegacyMultiLocation) &&
			professionalAvailable);
	// Trial banner stays up across an in-trial Trial401 → Basic402 upgrade,
	// so we gate on trial-billing-period (LastBilledDate) rather than planId alone.
	const userIsOnTrialPlan =
		planDetails?.planId === PlanIds.Trial401 || isInTrialBillingPeriod();
	// These flags come from the hook so they reflect optimistic updates (no
	// full page refresh required to flip the banner copy).
	const {
		pendingBasicOnTrial,
		currentIsBasic,
		pendingIsMultiLocation,
		isOnProfessional,
		currentIsLegacy,
	} = derived;
	const trialEndDate = nextBillingDate
		? (Util as any).formatDateReadable(nextBillingDate)
		: '';
	// Calculate days remaining in the trial period.
	// nextBillingDate is the date the trial ends (first real billing date).
	// Subtract today, convert ms to days (864e5 = 24*60*60*1000), floor so we
	// never show more days than actually remain, and clamp at 0.
	const today = new Date();
	const endDate = nextBillingDate ? new Date(nextBillingDate) : today;
	const trialDaysRemaining = Math.max(
		0,
		Math.floor((endDate.getTime() - today.getTime()) / 864e5)
	);
	const showTrialBanner =
		isNewLineup &&
		!currentIsLegacy &&
		userIsOnTrialPlan &&
		!isOnProfessional &&
		!pendingIsMultiLocation &&
		!closeRequestedDate;

	const renderClosedAccountMessage = () => (
		<SectionBody>
			<p>
				Your Stamps.com account has been closed. If you have any questions about
				your account status, please contact Customer Support.
			</p>
		</SectionBody>
	);

	const handleConfirmPlanChange = async () => {
		const ok = await actions.confirmPlanChange();
		if (ok) showModal({ component: PlanUpdatedSuccessModal });
	};

	const handleLegacyUpdatePlan = (plan: Plan) => {
		showModal({
			component: LegacyPlanChangeConfirmationModal,
			data: {
				newPlanName: plan.Name,
				newPlanId: plan.PlanId,
				newPlanPrice: plan.Price,
			},
		});
	};

	const handleContactSupport = () => {
		showModal({ component: UpgradeToMultiLocationModal });
	};

	const handleReviewDeleteUsers = () => {
		const { users, activeUsers, nextBillingDate } = state;
		const { selectedSeats } = derived;
		const overflowCount = activeUsers - selectedSeats;

		showModal({
			component: ReviewDeleteUsersModal,
			data: {
				users,
				overflowCount,
				nextBillingDate,
				onDeleteUsers: actions.deleteUsersAndConfirm,
			},
		});
	};

	const renderPlans = () => {
		const {
			plans,
			currentPlanId,
			selectedPlanId,
			seatsByPlan,
			isLoading,
			confirmationState,
			nextBillingDate,
			currentSeats,
		} = state;

		const {
			hasChanges,
			isOverflow,
			isSelectedMultiLocation,
			isDowngrade,
			isOnTrialPlan,
			currentPlanName,
			selectedPlan,
			selectedSeats,
			includedSeats,
			additionalSeats,
			totalPrice,
			seatPrice,
		} = derived;

		// New lineup keeps the natural plan order (Basic → Professional → Multi-Location);
		// legacy flow puts the current plan first.
		const orderedPlans = isNewLineup
			? plans
			: [
					...plans.filter(p => p.PlanId === currentPlanId),
					...plans.filter(p => p.PlanId !== currentPlanId),
				];

		const layoutClass = isNewLineup
			? 'subscription-plan-layout subscription-plan-layout--horizontal'
			: 'subscription-plan-layout subscription-plan-layout--single-column';

		const renderCard = (plan: Plan) => {
			const isCurrentPlan = plan.PlanId === currentPlanId;
			const isSelected = plan.PlanId === selectedPlanId;
			// Legacy plan cards rendered inside the new-lineup grid — disabled
			// seat stepper, with copy nudging the user toward picking a new-lineup
			// plan to add more user seats.
			const isLegacyPlan = NEW_LINEUP_PLAN_IDS.indexOf(plan.PlanId) === -1;

			return (
				<SubscriptionCard
					key={plan.PlanId}
					plan={plan}
					isCurrentPlan={isCurrentPlan}
					isSelected={isSelected}
					seatCount={
						isLegacyPlan
							? currentSeats
							: (seatsByPlan[plan.PlanId] ?? plan.NumberOfLocations ?? 1)
					}
					onSelect={actions.selectPlan}
					onSeatChange={actions.setSeats}
					seatPrice={seatPrice}
					focusSeatInput={focusSeats}
					cardRef={isCurrentPlan ? currentPlanCardRef : undefined}
					readOnly={readOnly}
					showUpdateButton={!isNewLineup && !isCurrentPlan && !readOnly}
					onUpdatePlan={
						isNewLineup
							? handleConfirmPlanChange
							: () => handleLegacyUpdatePlan(plan)
					}
					isUpdating={isSelected && confirmationState !== 'idle'}
					confirmationState={confirmationState}
					isCancelled={Boolean(closeRequestedDate)}
					isNewLineup={isNewLineup}
					isLegacyPlan={isLegacyPlan}
				/>
			);
		};

		return (
			<>
				{showTrialBanner && (
					<InlineMessage
						type="information"
						dataqa="subscription-plan-trial-banner"
						className="subscription-plan-trial-banner"
						text={
							pendingBasicOnTrial ? (
								// Trial A Phase 2, Basic committed in-product (COPY B)
								<>
									<strong>
										{`You selected the Basic plan during your risk-free trial ending in ${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'}.`}
									</strong>
									{` Switch to the Professional plan before ${trialEndDate} to avoid losing access to unlimited prints and advanced features.`}
								</>
							) : currentIsBasic ? (
								// Trial B, Basic chosen on marketing
								<>
									<strong>
										{`Your risk-free Basic trial ends in ${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'}.`}
									</strong>
									{` Switch to the Professional plan before ${trialEndDate} for advanced features, unlimited prints, and more user seats.`}
								</>
							) : (
								// Trial A Phase 1, no committed plan (COPY A)
								<>
									<strong>
										{`Your risk-free Professional trial ends in ${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'}.`}
									</strong>
									{` Select Professional plan before ${trialEndDate} to keep access to advanced features, unlimited prints, and more user seats.`}
								</>
							)
						}
					/>
				)}
				{closeRequestedDate &&
					(isNewLineup ? (
						<CancellationBanner
							closeRequestedDate={closeRequestedDate}
							nextBillingDate={nextBillingDate ?? ''}
							isTrialAccount={userIsOnTrialPlan}
						/>
					) : (
						<PendingPlanClosingDisclaimer
							accountInfo={Session.getAccountInfo()}
						/>
					))}
				<div className={layoutClass}>
					<div className="subscription-plan-left-column">
						{isNewLineup ? (
							<>
								<div className="subscription-plan-main-content">
									<div className="subscription-plan-cards">
										{orderedPlans.map(renderCard)}
									</div>

									{!currentIsLegacy &&
										plans.some(p => p.PlanId === PlanIds.Basic402) &&
										plans.some(p => p.PlanId === PlanIds.Professional403) &&
										plans.some(p => p.PlanId === PlanIds.MultiLocation404) && (
											<PlanFeaturesTable
												selectedPlanId={state.selectedPlanId}
												onSelectPlan={actions.selectPlan}
											/>
										)}
								</div>

								{!readOnly && (
									<CloseAccountSection
										onNavigate={onNavigate}
										flags={flags}
										asLink
										hideCancellation={Boolean(closeRequestedDate)}
									/>
								)}
							</>
						) : (
							<>
								{orderedPlans.map(renderCard)}

								<div className="subscription-info-text">
									<p>
										Mailing and shipping from multiple locations? Contact us
										about our{' '}
										<a
											href="https://www.stamps.com/enterprise/"
											target="_blank"
											rel="noreferrer"
										>
											Multi-Location Plan.
										</a>
									</p>
								</div>

								{!readOnly && (
									<CloseAccountSection
										onNavigate={onNavigate}
										flags={flags}
										hideCancellation={Boolean(closeRequestedDate)}
									/>
								)}
							</>
						)}
					</div>

					{isNewLineup && (
						<div className="subscription-plan-right-column">
							<PlanSummaryCard
								isSeatOnlyChange={
									selectedPlanId === currentPlanId &&
									selectedSeats !== state.currentSeats &&
									!currentIsLegacy
								}
								planName={selectedPlan?.Name ?? ''}
								planPrice={selectedPlan?.Price ?? 0}
								selectedSeats={selectedSeats}
								includedSeats={includedSeats}
								additionalSeats={additionalSeats}
								seatPrice={seatPrice}
								totalPrice={totalPrice}
								hasChanges={hasChanges}
								isOverflow={isOverflow}
								isMultiLocation={isSelectedMultiLocation}
								isDowngrade={isDowngrade}
								isOnTrialPlan={isOnTrialPlan}
								currentPlanName={currentPlanName}
								confirmationState={confirmationState}
								nextBillingDate={nextBillingDate}
								currentSeats={currentSeats}
								onConfirm={handleConfirmPlanChange}
								onReset={actions.resetToCurrentPlan}
								onReviewDeleteUsers={handleReviewDeleteUsers}
								onContactSupport={handleContactSupport}
								readOnly={readOnly}
								isCancelled={Boolean(closeRequestedDate)}
								currentIsLegacy={currentIsLegacy}
								isNewLineup={isNewLineup}
							/>
						</div>
					)}
				</div>

				{isLoading && <Spinner msg="Updating plan..." />}
			</>
		);
	};

	return (
		<>
			{readOnly && (
				<InlineMessage
					type="information"
					text="You don't have permission to modify the subscription plan. Contact your account admin to make changes."
					dataqa="subscription-plan-readonly-banner"
				/>
			)}
			<div
				className={`account-manage-subscription-plan${showTrialBanner || closeRequestedDate ? ' account-manage-subscription-plan--has-top-banner' : ''}${currentIsLegacy ? ' account-manage-subscription-plan--legacy' : ''}`}
			>
				<Section>
					<Typography.H1 className="account-title">
						Subscription Plan
					</Typography.H1>
					{accountStatus === AccountStatus.PendingClosure
						? renderClosedAccountMessage()
						: renderPlans()}
				</Section>
			</div>
		</>
	);
};

export default ManageSubscriptionPlan;
